import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Clock, Zap, RefreshCw, Check, X } from "lucide-react";
import { useDebouncedAction } from "@/hooks/useDebouncedAction";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString();
}

export default function SyncSchedulerPanel() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    const [s, l] = await Promise.all([
      api.get("/sync-scheduler/status"),
      api.get("/sync-scheduler/log?limit=10"),
    ]);
    setStatus(s.data);
    setLog(l.data);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const togglePaused = async (nextPaused) => {
    try {
      const { data } = await api.patch("/sync-scheduler/config", { paused: nextPaused });
      setStatus(data);
      toast.success(nextPaused ? "Scheduler paused" : "Scheduler resumed");
    } catch {
      toast.error("Could not update scheduler");
    }
  };

  const setInterval30 = async () => {
    await api.patch("/sync-scheduler/config", { tick_seconds: 30 * 60 });
    load();
    toast.success("Interval → 30 min");
  };

  const trigger = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post("/sync-scheduler/trigger");
      toast.success(`Ran ${data.syncs} sync(s) across ${data.tenants} tenant(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggering(false);
    }
  };

  const dTrigger = useDebouncedAction(trigger, 5000);

  if (!status) return null;

  return (
    <div className="border border-gray-200 bg-white rounded-sm p-8" data-testid="scheduler-panel">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 mt-1 text-gray-500" strokeWidth={1.5} />
          <div>
            <div className="overline text-gray-400 mb-1">Auto-Sync · Every 30 min</div>
            <div className="font-heading text-lg font-medium">Sync Scheduler</div>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
              Iterates all tenants with configured HubSpot / Salesforce credentials and refreshes their deals in the background. Also refreshes OAuth tokens automatically.
            </p>
          </div>
        </div>
        <span
          className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${
            status.running && !status.paused
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-gray-50 text-gray-500 border-gray-200"
          }`}
          data-testid="scheduler-state"
        >
          {status.running && !status.paused ? "RUNNING" : status.paused ? "PAUSED" : "STOPPED"}
        </span>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Interval</div>
          <div className="metric-value text-2xl">{Math.round(status.tick_seconds / 60)}<span className="text-xs text-gray-400"> min</span></div>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Ticks total</div>
          <div className="metric-value text-2xl">{status.ticks_total}</div>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Syncs total</div>
          <div className="metric-value text-2xl">{status.syncs_total}</div>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Last tick</div>
          <div className="text-sm font-mono truncate">{fmtTime(status.last_tick_at)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mb-5">
        <div className="flex items-center gap-3">
          <span className="overline text-gray-400 text-[10px]">Auto-sync enabled</span>
          <Switch
            checked={!status.paused}
            onCheckedChange={(v) => togglePaused(!v)}
            data-testid="scheduler-toggle"
          />
        </div>
        <Button
          onClick={dTrigger.run}
          disabled={dTrigger.disabled}
          variant="outline"
          size="sm"
          className="rounded-sm h-8 gap-2"
          data-testid="scheduler-trigger-btn"
        >
          <Zap className={`w-3 h-3 ${triggering ? "animate-pulse" : ""}`} />
          {dTrigger.cooldownRemaining > 0 ? `Wait ${dTrigger.cooldownRemaining}s` : "Run all syncs now"}
        </Button>
      </div>

      {log.length > 0 && (
        <div>
          <div className="overline text-gray-400 mb-2 text-[10px]">Recent runs</div>
          <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
            {log.map((entry) => (
              <div key={entry.id} className="px-3 py-2 flex items-center gap-3 text-xs" data-testid={`log-${entry.id}`}>
                {entry.ok ? (
                  <Check className="w-3 h-3 text-green-600 flex-shrink-0" strokeWidth={2.5} />
                ) : (
                  <X className="w-3 h-3 text-red-600 flex-shrink-0" strokeWidth={2.5} />
                )}
                <span className="font-mono text-gray-400 w-20">{fmtTime(entry.created_at)}</span>
                <span className="font-mono text-gray-700 min-w-[110px]">{entry.provider}</span>
                <span className="text-gray-500 truncate">
                  {entry.ok ? `+${entry.imported ?? 0} · ~${entry.updated ?? 0}` : entry.error?.slice(0, 60)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
